import React from 'react';
import T from './theme';
import CollapsibleCard from './CollapsibleCard';
import { extractError, optionalNumber, formatPace } from './utils';

export default function RecoveryCard({
  recovery, nextWorkout, todayBands,
  checkinInput, setCheckinInput, checkinReply, setCheckinReply,
  checkinBusy, setCheckinBusy,
  sleepScoreInput, setSleepScoreInput,
  onRecover, api, setError,
  fetchRecovery, fetchNextWorkout,
}) {
  return (
    <CollapsibleCard title="Recovery" accentColor={T.color.accentRecovery}>
      <p>
        <button type="button" onClick={onRecover} style={{ marginRight: 8 }}>
          Mark recovered
        </button>
        <span style={{ color: T.color.textLight, fontSize: T.font.sm }}>
          Clear remaining fatigue from past sessions. New runs still count.
        </span>
      </p>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          const body = checkinInput.trim();
          if (body.length < 2) {
            setError("Say a little about how you feel or what you\u2019re thinking for the next run.");
            return;
          }
          setCheckinBusy(true);
          try {
            const res = await api.post('/me/checkin', { body });
            setCheckinReply(res.data?.reply || '');
            setCheckinInput('');
            if (res.data?.recovery) fetchRecovery();
            await fetchNextWorkout();
            setError(null);
          } catch (err) {
            setError("Failed to save how you\u2019re feeling: " + extractError(err));
          } finally {
            setCheckinBusy(false);
          }
        }}
        style={{ margin: '0 0 16px' }}
      >
        <label style={{ display: 'block' }}>
          How are you feeling? What's the next workout looking like in your head?
          <textarea
            value={checkinInput}
            onChange={(e) => setCheckinInput(e.target.value)}
            rows={3}
            placeholder="Didn't sleep. Maybe just 20 easy minutes… or I feel snappy and want strides."
            style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 6 }}
          />
        </label>
        <button type="submit" disabled={checkinBusy} style={{ marginTop: 8 }}>
          {checkinBusy ? 'Thinking…' : 'Check in'}
        </button>
        {checkinReply && (
          <p style={{ color: T.color.textSecondary, fontStyle: 'italic', margin: '10px 0 0' }}>
            {checkinReply}
          </p>
        )}
      </form>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          const n = optionalNumber(sleepScoreInput);
          if (n == null || n < 0 || n > 100) {
            setError('Sleep score should be 0–100 (Garmin).');
            return;
          }
          try {
            await api.patch('/me', { sleep_score: n });
            await fetchRecovery();
            await fetchNextWorkout();
            setError(null);
          } catch (err) {
            setError('Failed to save sleep score: ' + extractError(err));
          }
        }}
        style={{ margin: '0 0 12px' }}
      >
        <label style={{ display: 'block' }}>
          Last night's Garmin sleep score
          <span style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
            <input
              type="number"
              min="0"
              max="100"
              value={sleepScoreInput}
              onChange={(e) => setSleepScoreInput(e.target.value)}
              placeholder="e.g. 41"
              style={{ width: 80 }}
            />
            <button type="submit">Apply</button>
          </span>
        </label>
        <span style={{ color: T.color.textLight, fontSize: T.font.sm }}>
          A score of 41 is a rough night (~6 h extra recovery).
        </span>
      </form>
      {recovery ? (
        <>
          <p style={{ fontSize: T.font.xxl, margin: '8px 0' }}>
            {recovery.sleep_debt_hours > 0
              ? `~${recovery.remaining_hours} h remaining`
              : recovery.strava_recovered_pct == null
                ? (recovery.recovered ? 'Recovered' : `~${recovery.remaining_hours} h remaining`)
                : `${recovery.strava_recovered_pct}% recovered`}
          </p>
          <p style={{ color: T.color.textMuted }}>
            {recovery.strava_remaining_hours > 0
              ? `~${recovery.strava_remaining_hours} h remaining from Strava`
              : 'Strava estimate: fully recovered'}
            {recovery.last_session?.name
              ? ` (${recovery.last_session.name}, ${recovery.last_session.hours_ago} h ago${
                  recovery.last_session.intensity
                    ? `, ${recovery.last_session.intensity}`
                    : ''
                }${
                  recovery.last_session.pct_lthr != null
                    ? ` ${Math.round(recovery.last_session.pct_lthr * 100)}% threshold`
                    : recovery.last_session.hr_reserve != null
                      ? ` ${Math.round(recovery.last_session.hr_reserve * 100)}% HR reserve`
                      : ''
                })`
              : ''}
          </p>
          {recovery.ran_today ? (
            <p style={{ color: T.color.textMuted }}>Already ran today. Next session is for tomorrow.</p>
          ) : (
            <p style={{ color: T.color.textMuted }}>No run yet today — next workout is for today.</p>
          )}
          {recovery.load_meter && (
            <div style={{ margin: '12px 0 8px' }}>
              <div style={{ fontSize: T.font.sm, color: '#444', marginBottom: 6 }}>
                Training load
                {recovery.load_meter.zone === 'high'
                  ? ' · very high'
                  : recovery.load_meter.zone === 'elevated'
                    ? ' · high'
                    : ''}
              </div>
              {[
                ['Last session', recovery.load_meter.last_session_pct, recovery.load_meter.last_session_hours, T.color.meterDefault],
                ['Cumulative (4 days)', recovery.load_meter.stacked_pct, recovery.load_meter.stacked_hours,
                  recovery.load_meter.zone === 'high' ? T.color.warning : recovery.load_meter.zone === 'elevated' ? T.color.warningElevated : T.color.meterNormal],
              ].map(([label, pct, hours, color]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ width: 118, fontSize: T.font.xs, color: T.color.textMuted }}>{label}</span>
                  <div style={{ flex: 1, height: 8, background: '#eee', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.max(0, Math.min(100, Number(pct) || 0))}%`, height: '100%', background: color }} />
                  </div>
                  <span style={{ width: 48, fontSize: T.font.xs, color: T.color.textMuted, textAlign: 'right' }}>
                    {hours == null ? '—' : `~${hours} h`}
                  </span>
                </div>
              ))}
              {recovery.load_meter.kudos && (
                <p style={{ color: T.color.success, fontSize: T.font.sm, margin: '8px 0 0', fontWeight: 500 }}>
                  {recovery.load_meter.kudos}
                </p>
              )}
              <p style={{ color: T.color.textMuted, fontSize: T.font.sm, margin: '6px 0 0' }}>{recovery.load_meter.note}</p>
            </div>
          )}
          <p style={{ color: T.color.textMuted }}>
            At {recovery.ready_label || 'tomorrow morning'}:{' '}
            {recovery.recovered_pct_at_ready == null
              ? '—'
              : `${recovery.recovered_pct_at_ready}% recovered`}
            {recovery.remaining_hours_at_ready > 0
              ? ` (~${recovery.remaining_hours_at_ready} h remaining)`
              : ''}
            {recovery.hours_until_ready != null ? ` · ${recovery.hours_until_ready} h from now` : ''}
          </p>
          <p>{recovery.suggested_note}</p>
          {nextWorkout?.rungs?.length > 0 && (() => {
            const recRung = nextWorkout.rungs.find((w) => w.rung === 'recommended');
            const protectRung = nextWorkout.rungs.find((w) => w.rung === 'protect');
            if (!recRung) return null;
            const sameProtect =
              protectRung &&
              protectRung.name === recRung.name &&
              protectRung.duration_display === recRung.duration_display;
            return (
              <p style={{ color: T.color.textMuted, fontSize: T.font.sm }}>
                Suggested workout: {recRung.name}
                {recRung.duration_display ? ` ${recRung.duration_display}` : ''}
                {!sameProtect && protectRung
                  ? ` (conservative option: ${protectRung.name}${
                      protectRung.duration_display ? ` ${protectRung.duration_display}` : ''
                    }).`
                  : '.'}
              </p>
            );
          })()}
          {recovery.fitness_scale != null && recovery.fitness_scale < 0.95 && (
            <p style={{ color: T.color.textMuted, fontSize: T.font.sm, margin: '6px 0 0' }}>
              Easy volume is scaled to your recent training
              {` (×${Number(recovery.fitness_scale).toFixed(2)})`}.
            </p>
          )}
          {todayBands.length > 0 && recovery.suggested_kind === 'easy_run' && recovery.suggested_minutes >= 15 && (
            <p>
              Easy pace {formatPace(todayBands[todayBands.length - 1].low)}–{formatPace(todayBands[todayBands.length - 1].high)}
              {todayBands.length > 1 ? ' (evening band)' : ''}
            </p>
          )}
          <p style={{ color: T.color.textLight, fontSize: T.font.sm }}>
            Intensity vs lactate threshold
            {recovery.lthr ? ` ${recovery.lthr}` : ''}
            {recovery.lthr_source ? ` (${String(recovery.lthr_source).replace('_', ' ')})` : ''}
            {recovery.hr_rest_used ? `, rest ${recovery.hr_rest_used}` : ''}.
            {recovery.vo2_max ? ` Watch VO2 ${recovery.vo2_max}.` : ''}
            {' '}Heat and a muggy day can make effort feel harder than heart rate suggests. Suggestion still aims to finish recovering before the next scheduled run
            {recovery.ready_source === 'tomorrow_morning' ? ' (or 6:00 AM if none).' : '.'}
            {recovery.recovered_manually ? ' Marked recovered; older fatigue is not counted.' : ''}
          </p>
        </>
      ) : (
        <p>Sync Strava to estimate recovery from recent runs.</p>
      )}
    </CollapsibleCard>
  );
}
